import {
    ContentBlock,
    ContentPage,
    usePortalMode,
    LoadStatus,
    Error,
    PortalSelectNew,
    AddressOptionComponentNew,
    ContentHeaderHeadline,
    usePortalStore,
    DocumentTitle,
} from '@servicetitan/cp-ui';
import { Divider } from '@servicetitan/design-system';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import { observer } from 'mobx-react';
import { FC, Fragment, PropsWithChildren, useEffect } from 'react';
import { AppSettingsStore } from '../../common/stores/app-settings.store';
import { BillingAccessStore } from '../../common/stores/billing-access.store';
import { DashboardApi } from '../api';
import { ProposalsApi } from '../api/proposal-api';
import { DashboardCustomerAddressesStore } from '../stores/dashboard-customer-addresses.store';
import { DashboardInvoicesStore } from '../stores/dashboard-invoices.store';
import { DashboardStore } from '../stores/dashboard.store';
import { EstimatesFiltersStore } from '../stores/estimates-filters.store';
import { InvoicesSettingsFiltersStore } from '../stores/invoices-settings-filters.store';
import { ProposalDrawerStore } from '../stores/proposal-drawer.store';
import { DashboardInvoices } from './sections/dashboard-invoices';
import { DashboardProposals } from './sections/dashboard-proposals';
import { DashboardSignableDocuments } from './sections/dashboard-signable-documents';
import { AppointmentDrawer } from './sections/jobs-dashboard/appointment-drawer/components/appointment-drawer';
import { DashboardJobs } from './sections/jobs-dashboard/dashboard-jobs';
import { DashboardServiceAgreements } from './sections/service-agreement-dashboard/dashboard-service-agreements';

export const DashboardModuleProvider: FC<PropsWithChildren> = provide({
    singletons: [
        DashboardApi,
        DashboardStore,
        ProposalDrawerStore,
        EstimatesFiltersStore,
        ProposalsApi,
        InvoicesSettingsFiltersStore,
        DashboardInvoicesStore,
        DashboardCustomerAddressesStore,
    ],
    // eslint-disable-next-line react/jsx-no-useless-fragment
})(({ children }) => <Fragment>{children}</Fragment>);

export const DashboardModule: FC = () => {
    return (
        <DashboardModuleProvider>
            <DashboardModuleUnwrapped />
        </DashboardModuleProvider>
    );
};

export const DashboardModuleUnwrapped: FC = observer(() => {
    const mode = usePortalMode();
    const store = usePortalStore();
    const [
        { data, loadStatus, onLocationsChanged },
        { addressesDropdownState, addressesFieldState },
        estimatesFiltersStore,
        {
            invoiceEnabled,
            jobEnabled,
            jobFilterSettings,
            onlineEstimatesEnabled,
            proposalsEstimatesEnabled,
            serviceAgreementsEnabled,
            signableDocumentsEnabled,
        },
        { isBillingEnabled, loaded: billingEnabledPropertyLoaded },
    ] = useDependencies(
        DashboardStore,
        DashboardCustomerAddressesStore,
        EstimatesFiltersStore,
        AppSettingsStore,
        BillingAccessStore
    );

    useEffect(() => {
        estimatesFiltersStore.init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const resetFilters = () => {
        addressesFieldState.onChangeHandler([]);
        onLocationsChanged();
    };

    const isLoading = loadStatus === LoadStatus.Loading;
    const isFiltered = !!addressesFieldState.value?.length;
    const hasBillingAccess = billingEnabledPropertyLoaded && isBillingEnabled;

    return (
        <ContentPage mode={mode} data-tracking-section="portal-dashboard-page">
            <DocumentTitle title={store.settings?.title ?? ''} />
            <AppointmentDrawer />
            <ContentBlock>
                <ContentHeaderHeadline>My Tasks</ContentHeaderHeadline>
                <Divider spacing={4} />
                <div className="m-b-3 d-f">
                    <PortalSelectNew
                        label="Service Location"
                        mobile={mode.mobile}
                        disabled={isLoading}
                        state={addressesDropdownState}
                        fieldState={addressesFieldState}
                        multiple={{ selectAll: true }}
                        triggerComponent="filter"
                        filterButtonProps={{
                            value: isFiltered ? addressesFieldState.value?.length : undefined,
                        }}
                        optionComponent={AddressOptionComponentNew}
                        onChange={onLocationsChanged}
                        width={mode.mobile ? 's' : 'm'}
                        searchPlaceholder="Search"
                    />
                </div>

                {hasBillingAccess && invoiceEnabled && (
                    <DashboardInvoices
                        filtered={isFiltered}
                        resetFilters={resetFilters}
                        className="m-t-4 m-b-4"
                    />
                )}

                {loadStatus === LoadStatus.Error ? (
                    <Error text="Unable to load dashboard data" />
                ) : (
                    <Fragment>
                        {hasBillingAccess && serviceAgreementsEnabled && (
                            <DashboardServiceAgreements
                                loading={isLoading}
                                serviceAgreements={data.serviceAgreements}
                                filtered={isFiltered}
                                resetFilters={resetFilters}
                            />
                        )}
                        {jobEnabled &&
                            (jobFilterSettings.inProgress || jobFilterSettings.scheduled) && (
                                <DashboardJobs
                                    loading={isLoading}
                                    jobs={data.jobs}
                                    filtered={isFiltered}
                                    resetFilters={resetFilters}
                                />
                            )}

                        {proposalsEstimatesEnabled &&
                            hasBillingAccess &&
                            onlineEstimatesEnabled && (
                                <DashboardProposals
                                    loading={isLoading}
                                    proposals={data.proposals}
                                    filtered={isFiltered}
                                    resetFilters={resetFilters}
                                    className="m-t-2"
                                />
                            )}

                        {signableDocumentsEnabled && (
                            <DashboardSignableDocuments className="m-t-2 m-b-4" />
                        )}
                    </Fragment>
                )}
            </ContentBlock>
        </ContentPage>
    );
});
